import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Flex,
  message,
  Table,
  Input,
  DatePicker,
  TreeSelect,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import {
  fetchExecutions,
  deleteExecution,
} from "../service/checklistExecution";
import { useNavigate } from "react-router-dom";
import { fetchBuilders } from "../../Configuration/ChecklistConfiguration/Service/checklistBuilder";
import { fetchProduct } from "../../../../configuration/module/Product/Service/product";
import { buildTreeData } from "../../../../configuration/module/Product/Component/product";

const { RangePicker } = DatePicker;

const ChecklistExecution = () => {
  const [executions, setExecutions] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [productList, setProductList] = useState([]);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [serialNumber, setSerialNumber] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("descend");

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // ================= FETCH =================
  const fetchData = async (
    pageNum = page,
    pageSize = size,
    filters = {},
    field = sortField,
    order = sortOrder
  ) => {
    setLoading(true);
    try {
      const res = await fetchExecutions({
        page: pageNum - 1,
        size: pageSize,
        search: filters.serialNumber ?? serialNumber ?? "",
        productId: filters.productId ?? selectedProduct ?? undefined,
        fromDate:
          filters.fromDate ??
          (dateRange?.length === 2 ? dateRange[0] : undefined),
        toDate:
          filters.toDate ??
          (dateRange?.length === 2 ? dateRange[1] : undefined),
        sort: field,
        desc: order === "descend",
      });

      setExecutions(res.data?.content || []);
      setTotal(res.data?.totalElements || 0);
      setPage(pageNum);
      setSize(pageSize);
      setSortField(field);
      setSortOrder(order);
    } catch {
      message.error("Error fetching executions");
    } finally {
      setLoading(false);
    }
  };

  // ================= INIT LOAD =================
  useEffect(() => {
    fetchData();
    fetchBuilders()
      .then((res) => setBuilders(res.data || []))
      .catch(() => message.error("Error fetching builders"));

    fetchProduct()
      .then((res) => setProductList(buildTreeData(res.data)))
      .catch(() => message.error("Error fetching products"));
  }, []);

  // ================= ACTIONS =================
  const deleteData = async (id) => {
    try {
      await deleteExecution(id);
      message.success("Execution deleted successfully");
      fetchData(page, size);
    } catch {
      message.error("Error deleting execution");
    }
  };

  const applyFilters = () => {
    fetchData(1, size);
  };

  const resetFilters = () => {
    setSerialNumber("");
    setSelectedProduct(null);
    setDateRange([]);

    fetchData(
      1,
      size,
      {
        serialNumber: "",
        productId: undefined,
        fromDate: undefined,
        toDate: undefined,
      },
      sortField,
      sortOrder
    );
  };

  // ================= COLUMNS =================
  const columns = [
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      sorter: true,
      sortOrder: sortField === "serialNumber" ? sortOrder : null,
    },
    {
      title: "Product",
      dataIndex: "checklistId",
      key: "product",
      sorter: true,
      sortOrder: sortField === "checklistId" ? sortOrder : null,
      render: (id) => builders.find((b) => b.id === id)?.productName || "-",
    },
    {
      title: "Checklist",
      dataIndex: "checklistId",
      key: "checklist",
      sorter: true,
      sortOrder: sortField === "checklistId" ? sortOrder : null,
      render: (id) => builders.find((b) => b.id === id)?.checklistName || "-",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder: sortField === "createdAt" ? sortOrder : null,
      render: (val) => (val ? dayjs(val).format("DD MMM YYYY HH:mm:ss") : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      sortOrder: sortField === "updatedAt" ? sortOrder : null,
      render: (val) => (val ? dayjs(val).format("DD MMM YYYY HH:mm:ss") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <>
          <FolderOpenOutlined
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => navigate(`view/${record.id}`)}
          />
          <EditOutlined
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => navigate(`edit/${record.id}`)}
          />
          <DeleteOutlined
            style={{ cursor: "pointer" }}
            onClick={() => deleteData(record.id)}
          />
        </>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>
            Checklist Execution
          </div>
        </Flex>

        {/* Filters */}
        <Flex gap={12} style={{ marginBottom: 16, flexWrap: "wrap" }}>
          <Input
            placeholder="Search"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            style={{ width: 200 }}
          />

          <TreeSelect
            value={selectedProduct}
            onChange={setSelectedProduct}
            treeData={productList}
            placeholder="Select product"
            allowClear
            style={{ width: 220 }}
          />

          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: 260 }}
          />

          <Button type="primary" onClick={applyFilters}>
            Apply
          </Button>
          <Button onClick={resetFilters}>Reset</Button>

          <Flex flex={1} justify="end">
            <Button
              type="primary"
              style={{ fontWeight: 600 }}
              onClick={() => navigate("add")}
            >
              + Add
            </Button>
          </Flex>
        </Flex>

        {/* Table */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={executions}
          bordered
          scroll={{ x: "max-content" }}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={(pagination, filters, sorter) => {
            const field = sorter.field || sortField;
            const order = sorter.order || sortOrder;

            fetchData(
              pagination.current,
              pagination.pageSize,
              {},
              field,
              order
            );
          }}
        />
      </div>
    </Spin>
  );
};

export default ChecklistExecution;
